/* Navigation and control glyphs, shared by the admin console and the trader
 * terminal.
 *
 * Inline SVG rather than an icon font or a package: they inherit `currentColor`
 * (so a section's hue and the white-on-blue active state both come for free),
 * they scale on a high-DPI monitor, and they add nothing to the bundle that a
 * tree-shaker has to chase.
 *
 * All are drawn on a 24-unit grid with a 1.9 stroke and round joins, so a row
 * of them reads as one set rather than a collection.
 */
import type { CSSProperties } from 'react'

export type IconName =
  | 'grid' | 'pulse' | 'orders' | 'bolt' | 'layers' | 'clock' | 'trend'
  | 'users' | 'wallet' | 'briefcase' | 'activity' | 'award'
  | 'puzzle' | 'cpu' | 'history'
  | 'rocket' | 'gauge' | 'waves' | 'server'
  | 'shield' | 'sliders' | 'alert' | 'power'
  | 'link' | 'building' | 'plug' | 'tag'
  | 'bell' | 'bug' | 'file' | 'archive'
  | 'check' | 'database' | 'report' | 'mail'
  | 'org' | 'key' | 'lock' | 'settings'
  | 'store' | 'sparkles' | 'code' | 'blocks' | 'headset'
  | 'sun' | 'moon' | 'help' | 'bulb' | 'info' | 'search' | 'filter' | 'sort'
  | 'refresh' | 'expand' | 'external' | 'download' | 'columns'
  | 'chevron-down' | 'chevron-left' | 'chevron-right' | 'menu' | 'x' | 'plus'
  | 'play' | 'stop' | 'logout' | 'user' | 'home' | 'candles' | 'flask'
  | 'book' | 'chat' | 'keyboard' | 'target' | 'coins' | 'terminal'

const PATHS: Record<IconName, string> = {
  grid:      'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  pulse:     'M3 12h4l2.5-7 4 14L16 12h5',
  orders:    'M5 3h11l3 3v15H5zM8 9h8M8 13h8M8 17h5',
  bolt:      'M13 2 4 14h7l-1 8 9-12h-7z',
  layers:    'M12 3 3 8l9 5 9-5zM3 13l9 5 9-5M3 17.5l9 5 9-5',
  clock:     'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3.5 2',
  trend:     'M3 17l6-6 4 4 8-8M17 7h4v4',
  users:     'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM2 21c0-3.9 3.1-7 7-7s7 3.1 7 7M17 4.5a3.6 3.6 0 0 1 0 7M19 14.5c1.9.9 3 2.6 3 4.5',
  wallet:    'M3 7a2 2 0 0 1 2-2h12v4M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7H7a2 2 0 0 1-4 0zM17.5 13.5h.01',
  briefcase: 'M3 8h18v12H3zM8 8V5h8v3M3 13h18',
  activity:  'M4 20V9M9.3 20V4M14.7 20v-7M20 20V6',
  award:     'M12 15a6 6 0 1 0 0-12 6 6 0 0 0 0 12zM8.5 13.5 7 22l5-2.6L17 22l-1.5-8.5',
  puzzle:    'M10 4a2 2 0 1 1 4 0v1h4v4h1a2 2 0 1 1 0 4h-1v4h-4v-1a2 2 0 1 0-4 0v1H6v-4H5a2 2 0 1 1 0-4h1V5h4z',
  cpu:       'M7 7h10v10H7zM9.5 2.5v3M14.5 2.5v3M9.5 18.5v3M14.5 18.5v3M2.5 9.5h3M2.5 14.5h3M18.5 9.5h3M18.5 14.5h3',
  history:   'M3.5 12a8.5 8.5 0 1 0 2.6-6.1M3 4v5h5M12 8v4.5l3 1.8',
  rocket:    'M12 2c3.5 2.4 5.5 6.2 5.5 10.5L15 16H9l-2.5-3.5C6.5 8.2 8.5 4.4 12 2zM12 10.5h.01M8.5 17c-1.6.9-2.3 2.6-2 5 2.4.3 4.1-.4 5-2M15.5 17c1.6.9 2.3 2.6 2 5-2.4.3-4.1-.4-5-2',
  gauge:     'M4.5 18a9 9 0 1 1 15 0M12 13.5 16 9',
  waves:     'M2 8c2.5-2.5 4.5-2.5 7 0s4.5 2.5 7 0 4.5-2.5 6 0M2 14c2.5-2.5 4.5-2.5 7 0s4.5 2.5 7 0 4.5-2.5 6 0M2 20c2.5-2.5 4.5-2.5 7 0',
  server:    'M3 4h18v6H3zM3 14h18v6H3zM7 7h.01M7 17h.01M11 7h5M11 17h5',
  shield:    'M12 2.5 20 6v6c0 4.6-3.2 8.3-8 9.5-4.8-1.2-8-4.9-8-9.5V6z',
  sliders:   'M4 7h9M17 7h3M4 12h3M11 12h9M4 17h11M19 17h1M15 4.5v5M9 9.5v5M17 14.5v5',
  alert:     'M12 3 2 20h20zM12 9v5M12 17.2h.01',
  power:     'M12 3v9M6.8 6.2a8 8 0 1 0 10.4 0',
  link:      'M10 13.5a4 4 0 0 0 5.7 0l3-3a4 4 0 1 0-5.7-5.7l-1.5 1.5M14 10.5a4 4 0 0 0-5.7 0l-3 3a4 4 0 1 0 5.7 5.7l1.5-1.5',
  building:  'M4 21V4l8-2v19M12 21V8l8 2v11M2 21h20M7 7h1M7 11h1M7 15h1M16 13h1M16 17h1',
  plug:      'M9 2v6M15 2v6M6 8h12v3a6 6 0 0 1-6 6 6 6 0 0 1-6-6zM12 17v5',
  tag:       'M3 12.5V4h8.5L21 13.5 12.5 22zM7.5 8h.01',
  bell:      'M12 3a6 6 0 0 1 6 6c0 4 1.5 5.5 2 6H4c.5-.5 2-2 2-6a6 6 0 0 1 6-6zM10 19a2 2 0 0 0 4 0',
  bug:       'M8 8a4 4 0 1 1 8 0v6a4 4 0 1 1-8 0zM8 11H3M21 11h-5M8 16l-4 3M16 16l4 3M8 6 5.5 4M16 6 18.5 4',
  file:      'M6 3h8l4 4v14H6zM14 3v4h4M9 12h6M9 16h6',
  archive:   'M3 4h18v4H3zM5 8v12h14V8M10 12h4',
  check:     'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM8 12.2l2.7 2.8L16 9.5',
  database:  'M12 3c4.4 0 8 1.3 8 3s-3.6 3-8 3-8-1.3-8-3 3.6-3 8-3zM4 6v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3',
  report:    'M6 3h8l4 4v14H6zM14 3v4h4M9.5 17v-3M12 17v-6M14.5 17v-4',
  mail:      'M3 5h18v14H3zM3 6l9 7 9-7',
  org:       'M9 3h6v4H9zM3 17h6v4H3zM15 17h6v4h-6zM12 7v4M6 17v-3h12v3',
  key:       'M15.5 3a5.5 5.5 0 1 0-3.9 9.4L3 21v0h3v-3h3v-3h2l1.6-1.6A5.5 5.5 0 0 0 15.5 3zM17 7.5h.01',
  lock:      'M5 11h14v10H5zM8 11V7.5a4 4 0 1 1 8 0V11M12 15v2',
  settings:  'M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4zM19.4 14.5a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1v.3a2 2 0 1 1-4 0v-.2a1.6 1.6 0 0 0-2.7-1.2l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0-1.1-2.7h-.3a2 2 0 1 1 0-4h.2a1.6 1.6 0 0 0 1.2-2.7l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 2.7-1.1v-.3a2 2 0 1 1 4 0v.2a1.6 1.6 0 0 0 2.7 1.2l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0 1.1 2.7h.3a2 2 0 1 1 0 4h-.2a1.6 1.6 0 0 0-1.5 1z',
  store:     'M4 4h16l1.5 5a3 3 0 0 1-5.8 1 3 3 0 0 1-5.8 0 3 3 0 0 1-5.8-1zM5 11v9h14v-9M10 20v-5h4v5',
  sparkles:  'M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9zM18.5 15l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8zM5.5 2l.6 1.6 1.6.6-1.6.6L5.5 6.4 4.9 4.8 3.3 4.2l1.6-.6z',
  code:      'M8.5 7.5 3 12l5.5 4.5M15.5 7.5 21 12l-5.5 4.5M13.5 4l-3 16',
  blocks:    'M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM17.5 14v7M14 17.5h7',
  headset:   'M4 13a8 8 0 1 1 16 0M4 13v3a2 2 0 0 0 2 2h1v-7H6a2 2 0 0 0-2 2zM20 13v3a2 2 0 0 1-2 2h-1v-7h1a2 2 0 0 1 2 2zM19 18v1a3 3 0 0 1-3 3h-3',
  sun:       'M12 16.5a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9zM12 2v2.5M12 19.5V22M2 12h2.5M19.5 12H22M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M4.9 19.1l1.8-1.8M17.3 6.7l1.8-1.8',
  moon:      'M20.5 14.5A8.5 8.5 0 0 1 9.5 3.5a8.5 8.5 0 1 0 11 11z',
  help:      'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM9.5 9.5a2.5 2.5 0 1 1 3.6 2.2c-.7.4-1.1 1-1.1 1.8v.5M12 17.2h.01',
  bulb:      'M9 18h6M10 21h4M8.5 14.5A6 6 0 1 1 15.5 14.5c-.6.6-1 1.3-1 2.5h-5c0-1.2-.4-1.9-1-2.5z',
  info:      'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 11v5M12 7.8h.01',
  search:    'M10.5 17a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13zM20 20l-4.8-4.8',
  filter:    'M4 6h16M7 12h10M10 18h4',
  sort:      'M7 4v16M7 20l-3-3M7 20l3-3M17 20V4M17 4l-3 3M17 4l3 3',
  refresh:   'M20 12a8 8 0 1 1-2.3-5.7M20 4v5h-5',
  expand:    'M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5',
  external:  'M14 4h6v6M20 4l-9 9M18 13v6H5V6h6',
  download:  'M12 4v11M7 10l5 5 5-5M4 19h16',
  columns:   'M4 5h16v14H4zM10 5v14M15 5v14',
  'chevron-down':  'M6 9l6 6 6-6',
  'chevron-left':  'M15 6l-6 6 6 6',
  'chevron-right': 'M9 6l6 6-6 6',
  menu:      'M4 7h16M4 12h16M4 17h16',
  x:         'M6 6l12 12M18 6 6 18',
  plus:      'M12 5v14M5 12h14',
  play:      'M7 4.5v15l12-7.5z',
  stop:      'M6 6h12v12H6z',
  logout:    'M10 4H5v16h5M14 8l4 4-4 4M18 12H9',
  user:      'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21c0-4 3.6-7 8-7s8 3 8 7',
  home:      'M3 11 12 4l9 7M5 10v10h5v-6h4v6h5V10',
  candles:   'M7 3v3M7 14v7M5 6h4v8H5zM17 3v6M17 17v4M15 9h4v8h-4z',
  flask:     'M9 3h6M10 3v6L4.5 19a1.5 1.5 0 0 0 1.3 2.2h12.4a1.5 1.5 0 0 0 1.3-2.2L14 9V3M7 15h10',
  book:      'M4 4h7a2 2 0 0 1 2 2v14a2 2 0 0 0-2-2H4zM20 4h-7a2 2 0 0 0-2 2v14a2 2 0 0 1 2-2h7z',
  chat:      'M4 5h16v11H9l-5 4z',
  keyboard:  'M3 6h18v12H3zM7 10h.01M11 10h.01M15 10h.01M7 14h10',
  target:    'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM12 12h.01',
  coins:     'M9 13a6 3 0 1 0 0-6 6 3 0 0 0 0 6zM3 10v4c0 1.7 2.7 3 6 3s6-1.3 6-3v-4M9 17v4c0 1.7 2.7 3 6 3M15 14c3.3 0 6-1.3 6-3M21 11v4c0 1.7-2.7 3-6 3',
  terminal:  'M4 4h16v16H4zM8 9l3 3-3 3M13 15h4',
}

export function Icon({ name, size = 17, style }: {
  name: IconName
  size?: number
  style?: CSSProperties
}) {
  return (
    <svg
      viewBox="0 0 24 24" width={size} height={size} aria-hidden="true"
      focusable="false"
      fill="none" stroke="currentColor" strokeWidth={1.9}
      strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0, display: 'block', ...style }}
    >
      <path d={PATHS[name]} />
    </svg>
  )
}
